import { z } from "zod";
import type { SocketType } from "../types";

/**
 * Shared helpers for the `famous_sql_query` step — lets users run their own
 * read-only SQL against the Famous Oracle database from a workflow.
 *
 * Both socket sides are dynamic and derived synchronously from config:
 *  - Input sockets come from the user-declared bind parameters.
 *  - Output sockets come from the column metadata detected by the "Run
 *    preview" action (persisted in config as `detectedColumns`), plus the
 *    always-present `rows` / `rowCount` sockets.
 *
 * Used by the backend socket dispatchers (`backend/src/workflows/sockets/`),
 * the workflow editor (socket regeneration on config change), and the config
 * renderer (bind-usage validation).
 */

export const FAMOUS_SQL_QUERY_STEP_TYPE = "famous_sql_query" as const;

/** Hard cap on rows a single run may return (also the config default). */
export const FAMOUS_SQL_QUERY_MAX_ROWS = 10_000;

/** Rows returned by the editor's "Run preview" action. */
export const FAMOUS_SQL_QUERY_PREVIEW_ROW_LIMIT = 50;

/**
 * Default Oracle round-trip timeout for ad-hoc Famous SQL (seconds).
 * Matches the 2-minute bound used by other Famous workflow step handlers.
 */
export const FAMOUS_SQL_QUERY_DEFAULT_TIMEOUT_SECONDS = 120;

/**
 * Hard max for `queryTimeoutSeconds`. Kept under the step's Temporal
 * `startToCloseTimeout` (10 minutes) so the driver aborts before the activity.
 */
export const FAMOUS_SQL_QUERY_MAX_TIMEOUT_SECONDS = 540;

/** Minimum allowed `queryTimeoutSeconds`. */
export const FAMOUS_SQL_QUERY_MIN_TIMEOUT_SECONDS = 1;

/** Bind parameter names double as input socket ids. */
export const FAMOUS_SQL_BIND_NAME_PATTERN = /^[a-z][a-z0-9_]*$/;

export const FAMOUS_SQL_PARAMETER_TYPES = ["string", "number", "date"] as const;
export type FamousSqlParameterType =
  (typeof FAMOUS_SQL_PARAMETER_TYPES)[number];

export const FAMOUS_SQL_COLUMN_TYPES = [
  "string",
  "number",
  "boolean",
  "json",
] as const;
export type FamousSqlColumnType = (typeof FAMOUS_SQL_COLUMN_TYPES)[number];

/** Output socket ids that always exist; detected columns may not use them. */
export const FAMOUS_SQL_QUERY_RESERVED_OUTPUT_SOCKET_IDS = [
  "rows",
  "rowCount",
] as const;

const reservedOutputSocketIdSet = new Set<string>(
  FAMOUS_SQL_QUERY_RESERVED_OUTPUT_SOCKET_IDS,
);

/**
 * Single bind-parameter entry. Shared by the step `configSchema` and the
 * lenient socket parsers (invalid stored entries are skipped via safeParse).
 */
export const famousSqlQueryParameterSchema = z.object({
  name: z.string().regex(FAMOUS_SQL_BIND_NAME_PATTERN),
  type: z.enum(FAMOUS_SQL_PARAMETER_TYPES).default("string"),
  description: z.string().optional(),
  required: z.boolean().default(true),
});

export type FamousSqlQueryParameter = z.infer<
  typeof famousSqlQueryParameterSchema
>;

/**
 * Single detected-column entry. Enforces non-empty Oracle column names and
 * rejects socket ids that collide with the always-present `rows` / `rowCount`
 * outputs.
 */
export const famousSqlQueryDetectedColumnSchema = z.object({
  /** Column name exactly as returned by Oracle (e.g. `ORDER_ID`). */
  name: z.string().min(1),
  /** Sanitized socket id derived from the column name (e.g. `order_id`). */
  socketId: z
    .string()
    .regex(FAMOUS_SQL_BIND_NAME_PATTERN)
    .refine((socketId) => !reservedOutputSocketIdSet.has(socketId), {
      message:
        'Detected column socket id may not be a reserved output id ("rows" or "rowCount")',
    }),
  type: z.enum(FAMOUS_SQL_COLUMN_TYPES).default("json"),
});

export type FamousSqlQueryDetectedColumn = z.infer<
  typeof famousSqlQueryDetectedColumnSchema
>;

function refineUniqueField<T>(
  field: keyof T & string,
  label: string,
): (items: T[], ctx: z.RefinementCtx) => void {
  return (items, ctx) => {
    const seen = new Set<string>();
    items.forEach((item, index) => {
      const value = item[field];
      if (typeof value !== "string") return;
      if (seen.has(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Duplicate ${label}: ${value}`,
          path: [index, field],
        });
        return;
      }
      seen.add(value);
    });
  };
}

/** Bind-parameter list used by the step configSchema (unique names). */
export const famousSqlQueryParametersSchema = z
  .array(famousSqlQueryParameterSchema)
  .superRefine(refineUniqueField("name", "parameters[].name"));

/** Detected-column list used by the step configSchema (unique socket ids). */
export const famousSqlQueryDetectedColumnsSchema = z
  .array(famousSqlQueryDetectedColumnSchema)
  .superRefine(refineUniqueField("socketId", "detectedColumns[].socketId"));

export function parseFamousSqlQueryParameters(
  config: Record<string, unknown>,
): FamousSqlQueryParameter[] {
  const raw = config.parameters;
  if (!Array.isArray(raw)) return [];

  const seenNames = new Set<string>();
  return raw.flatMap((entry): FamousSqlQueryParameter[] => {
    const parsed = famousSqlQueryParameterSchema.safeParse(entry);
    if (!parsed.success || seenNames.has(parsed.data.name)) return [];
    // Discard duplicates so input socket ids stay unique (edge wiring is by id).
    seenNames.add(parsed.data.name);
    return [parsed.data];
  });
}

export function parseFamousSqlQueryDetectedColumns(
  config: Record<string, unknown>,
): FamousSqlQueryDetectedColumn[] {
  const raw = config.detectedColumns;
  if (!Array.isArray(raw)) return [];

  const seenSocketIds = new Set<string>();
  return raw.flatMap((entry): FamousSqlQueryDetectedColumn[] => {
    const parsed = famousSqlQueryDetectedColumnSchema.safeParse(entry);
    if (!parsed.success || seenSocketIds.has(parsed.data.socketId)) return [];
    // Discard duplicates so output socket ids stay unique (edge wiring is by id).
    seenSocketIds.add(parsed.data.socketId);
    return [parsed.data];
  });
}

export interface FamousSqlQuerySocketDefinition {
  id: string;
  name: string;
  type: SocketType;
  required: boolean;
  description?: string;
}

function prettifySocketId(id: string): string {
  return id
    .split("_")
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

const PARAMETER_TYPE_TO_SOCKET_TYPE: Record<
  FamousSqlParameterType,
  SocketType
> = {
  string: "string",
  number: "number",
  // Date binds travel as ISO strings between steps; the handler converts to
  // a JS Date before binding (matches how other steps type date sockets).
  date: "string",
};

/**
 * One typed input socket per declared bind parameter. The socket id equals
 * the bind name, so wiring an edge into `order_number` feeds `:order_number`.
 */
export function generateFamousSqlQueryInputSockets(
  config: Record<string, unknown>,
): FamousSqlQuerySocketDefinition[] {
  return parseFamousSqlQueryParameters(config).map((parameter) => ({
    id: parameter.name,
    name: prettifySocketId(parameter.name),
    type: PARAMETER_TYPE_TO_SOCKET_TYPE[parameter.type],
    required: parameter.required,
    description:
      parameter.description ??
      `Value for the :${parameter.name} bind parameter`,
  }));
}

const COLUMN_TYPE_TO_ARRAY_SOCKET_TYPE: Record<
  FamousSqlColumnType,
  SocketType
> = {
  string: "array<string>",
  number: "array<number>",
  boolean: "array<boolean>",
  json: "array<json>",
};

/**
 * Output sockets: the always-present `rows` (array of row objects) and
 * `rowCount`, plus one typed `array<T>` socket per column detected via the
 * editor's "Run preview" action. Each column socket carries that column's
 * values across all returned rows, in row order.
 */
export function generateFamousSqlQueryOutputSockets(
  config: Record<string, unknown>,
): FamousSqlQuerySocketDefinition[] {
  const columnSockets = parseFamousSqlQueryDetectedColumns(config).map(
    (column): FamousSqlQuerySocketDefinition => ({
      id: column.socketId,
      name: prettifySocketId(column.socketId),
      type: COLUMN_TYPE_TO_ARRAY_SOCKET_TYPE[column.type],
      required: false,
      description: `Values of column "${column.name}" across all rows`,
    }),
  );

  return [
    {
      id: "rows",
      name: "Rows",
      type: "array<json>",
      required: true,
      description: "Result rows as objects keyed by column name",
    },
    {
      id: "rowCount",
      name: "Row Count",
      type: "number",
      required: true,
      description: "Number of rows returned (after the max-rows cap)",
    },
    ...columnSockets,
  ];
}

/**
 * Extract the bind parameter names referenced by a SQL statement, lowercased
 * and deduplicated. Comments and string literals are stripped first so a
 * `:name` inside a literal or comment does not count as a bind.
 */
export function extractSqlBindNames(sql: string): string[] {
  const stripped = stripSqlCommentsAndLiterals(sql);
  const names = new Set<string>();
  for (const match of stripped.matchAll(/:\s*([a-zA-Z][a-zA-Z0-9_]*)/g)) {
    names.add(match[1].toLowerCase());
  }
  return [...names];
}

/** Closing delimiter for Oracle q-quote paired openers; others close with themselves. */
const Q_QUOTE_CLOSE_BY_OPEN: Readonly<Record<string, string>> = {
  "[": "]",
  "{": "}",
  "<": ">",
  "(": ")",
};

function isSqlIdentifierChar(ch: string | undefined): boolean {
  if (ch === undefined) return false;
  return /[A-Za-z0-9_$]/.test(ch);
}

/** Oracle forbids space/tab/return as q-quote delimiters. */
function isValidQQuoteDelimiter(ch: string): boolean {
  return ch !== " " && ch !== "\t" && ch !== "\n" && ch !== "\r";
}

/**
 * True when `sql[i]` starts an Oracle alternative-quoted literal (`q'…'` / `Q'…'`),
 * not an identifier that merely ends in `q` before a normal string.
 */
function isQQuoteLiteralStart(sql: string, i: number): boolean {
  const ch = sql[i];
  if (ch !== "q" && ch !== "Q") return false;
  if (sql[i + 1] !== "'") return false;
  if (isSqlIdentifierChar(sql[i - 1])) return false;
  const openDelim = sql[i + 2];
  return openDelim !== undefined && isValidQQuoteDelimiter(openDelim);
}

/**
 * Index just past the closing `'` of a q-quoted literal whose opening delimiter
 * is at `openDelimIndex`. Unterminated literals consume through end of input.
 */
function endOfQQuoteLiteral(sql: string, openDelimIndex: number): number {
  const openDelim = sql[openDelimIndex];
  if (openDelim === undefined) return sql.length;
  const closeDelim = Q_QUOTE_CLOSE_BY_OPEN[openDelim] ?? openDelim;
  let j = openDelimIndex + 1;
  while (j < sql.length - 1) {
    if (sql[j] === closeDelim && sql[j + 1] === "'") {
      return j + 2;
    }
    j += 1;
  }
  return sql.length;
}

/**
 * Replace comments and string literals (including Oracle q-quoted literals) with
 * spaces, preserving everything else. Good enough for bind extraction and
 * keyword checks — not a full Oracle SQL parser.
 */
export function stripSqlCommentsAndLiterals(sql: string): string {
  let out = "";
  let i = 0;
  while (i < sql.length) {
    const twoChars = sql.slice(i, i + 2);
    if (twoChars === "--") {
      const end = sql.indexOf("\n", i);
      i = end === -1 ? sql.length : end;
      continue;
    }
    if (twoChars === "/*") {
      const end = sql.indexOf("*/", i + 2);
      i = end === -1 ? sql.length : end + 2;
      out += " ";
      continue;
    }
    // Must run before the plain `'` handler — q'[...]' begins with q then '.
    if (isQQuoteLiteralStart(sql, i)) {
      i = endOfQQuoteLiteral(sql, i + 2);
      out += " ";
      continue;
    }
    if (sql[i] === "'") {
      // Skip the literal; '' inside a literal is an escaped quote.
      let j = i + 1;
      while (j < sql.length) {
        if (sql[j] === "'" && sql[j + 1] === "'") {
          j += 2;
          continue;
        }
        if (sql[j] === "'") break;
        j += 1;
      }
      i = Math.min(j + 1, sql.length);
      out += " ";
      continue;
    }
    out += sql[i];
    i += 1;
  }
  return out;
}

/**
 * Derive a socket id from an Oracle column name: lowercase, non-identifier
 * characters collapsed to underscores, guaranteed to start with a letter and
 * to be unique against `taken` (and never a reserved output socket id).
 */
export function columnNameToSocketId(
  columnName: string,
  taken: ReadonlySet<string>,
): string {
  let base = columnName
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_{2,}/g, "_");
  if (!/^[a-z]/.test(base)) {
    base = base.length > 0 ? `col_${base}` : "col";
  }

  const reserved = new Set<string>([
    ...FAMOUS_SQL_QUERY_RESERVED_OUTPUT_SOCKET_IDS,
    ...taken,
  ]);
  if (!reserved.has(base)) return base;

  let suffix = 2;
  while (reserved.has(`${base}_${suffix}`)) suffix += 1;
  return `${base}_${suffix}`;
}
